"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { trackRecommendation, setNavigationSource } from "@/lib/recommendation-tracker";
import {
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  LinkIcon,
} from "lucide-react";
import CdnImage from "@/components/CdnImage";
import type { MarketLike, BuyPrices } from "@/hooks/useRealtimeMarketPrice";
import { useRelatedEvents } from "@/hooks/useRelatedEvents";

interface Market {
  id: string;
  question: string;
  groupItemTitle?: string | null;
  slug: string;
  conditionId: string;
  outcomes: string[];
  outcomePrices: number[];
  clobTokenIds: string[];
  lastTradePrice?: number;
  bestBid?: number;
  bestAsk?: number;
  acceptingOrders: boolean;
  active: boolean;
  closed: boolean;
  volume: number;
  liquidity: number;
  enableOrderBook: boolean;
  image?: string | null;
  oneDayPriceChange?: number | null;
  oneHourPriceChange?: number | null;
}

interface GenericEventSidebarProps {
  markets: Market[];
  eventSlug: string;
  eventTitle: string;
  onTrade: (params: {
    marketSlug: string;
    outcome: "YES" | "NO";
    outcomePrice: number;
    marketQuestion: string;
    actualOutcome?: string;
  }) => void;
  getRealtimePrice: (market: MarketLike) => [number, number];
  getBuyPrices: (market: MarketLike) => BuyPrices;
}

interface RelatedEvent {
  slug: string;
  title: string;
  image?: string | null;
  icon?: string | null;
  markets?: { outcomePrices?: number[] }[];
}

function getMarketLabel(market: Market): string {
  return market.groupItemTitle || market.question;
}

function formatPriceChange(change: number | null | undefined): {
  text: string;
  color: string;
  Icon: typeof TrendingUp;
} {
  if (change == null || change === 0) {
    return { text: "0%", color: "text-gray-500", Icon: Minus };
  }
  const pct = (change * 100).toFixed(1);
  if (change > 0) {
    return { text: `+${pct}%`, color: "text-green-400", Icon: TrendingUp };
  }
  return { text: `${pct}%`, color: "text-red-400", Icon: TrendingDown };
}

export default function GenericEventSidebar({
  markets,
  eventSlug,
  eventTitle,
  onTrade,
  getRealtimePrice,
  getBuyPrices,
}: GenericEventSidebarProps) {
  // Default selected market: the one with highest YES price
  const defaultIdx = useMemo(() => {
    let best = 0;
    let bestPrice = 0;
    markets.forEach((m, i) => {
      const [yes] = getRealtimePrice(m);
      if (yes > bestPrice) {
        bestPrice = yes;
        best = i;
      }
    });
    return best;
  }, [markets, getRealtimePrice]);

  const [selectedIdx, setSelectedIdx] = useState(defaultIdx);

  // Use shared hook for related events (deduplicates across multiple instances)
  const topMarketSlug = useMemo(() => {
    if (!markets.length) return null;
    return [...markets].sort((a, b) => b.volume - a.volume)[0]?.slug || null;
  }, [markets]);
  const { relatedEvents, loading: relatedLoading } = useRelatedEvents(topMarketSlug, eventSlug);

  const selectedMarket = markets[selectedIdx] || markets[0];
  const isMarketActive =
    selectedMarket?.active &&
    selectedMarket?.acceptingOrders &&
    !selectedMarket?.closed;

  // Sorted markets for the overview
  const sortedMarkets = useMemo(() => {
    return markets
      .map((m, i) => ({ market: m, originalIdx: i }))
      .sort((a, b) => {
        const [aYes] = getRealtimePrice(a.market);
        const [bYes] = getRealtimePrice(b.market);
        return bYes - aYes;
      });
  }, [markets, getRealtimePrice]);

  if (!markets.length) return null;

  const [yesPrice, noPrice] = getRealtimePrice(selectedMarket);
  const { buyYes, buyNo } = getBuyPrices(selectedMarket);

  return (
    <div className="space-y-4">
      {/* Section 1: Quick Trade */}
      <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[#1A1A1A]">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-gray-400" />
            <span className="text-white text-sm font-inter-tight font-bold">
              Quick Trade
            </span>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {/* Market selector */}
          {markets.length > 1 && (
            <select
              value={selectedIdx}
              onChange={(e) => setSelectedIdx(Number(e.target.value))}
              className="w-full bg-[#0f0f12] border border-[#1A1A1A] rounded-[5px] px-3 py-2 text-white text-sm font-satoshi focus:outline-none focus:border-gray-500 appearance-none cursor-pointer"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
              }}
            >
              {markets.map((m, i) => (
                <option key={m.id} value={i}>
                  {getMarketLabel(m)}
                </option>
              ))}
            </select>
          )}

          {/* Current price */}
          <div className="text-center py-2">
            <p className="text-gray-400 text-xs font-inter-tight mb-1">
              {markets.length === 1
                ? getMarketLabel(selectedMarket)
                : "Current probability"}
            </p>
            <p className="text-3xl font-inter-tight font-black text-white">
              {Math.round(yesPrice * 100)}%
            </p>
          </div>

          {/* Trade buttons */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                if (isMarketActive) {
                  onTrade({
                    marketSlug: selectedMarket.slug,
                    outcome: "YES",
                    outcomePrice: buyYes,
                    marketQuestion: selectedMarket.question,
                    actualOutcome:
                      selectedMarket.outcomes?.[0] || "Yes",
                  });
                }
              }}
              disabled={!isMarketActive}
              className={`py-3 rounded-[5px] font-inter-tight font-bold text-sm transition-colors ${
                isMarketActive
                  ? "bg-[#359a5e] hover:bg-[#2d8450] text-white"
                  : "bg-[#18181D] border border-[#1A1A1A] text-gray-600 cursor-not-allowed"
              }`}
            >
              Yes {(buyYes * 100).toFixed(1)}¢
            </button>
            <button
              onClick={() => {
                if (isMarketActive) {
                  onTrade({
                    marketSlug: selectedMarket.slug,
                    outcome: "NO",
                    outcomePrice: buyNo,
                    marketQuestion: selectedMarket.question,
                    actualOutcome:
                      selectedMarket.outcomes?.[1] || "No",
                  });
                }
              }}
              disabled={!isMarketActive}
              className={`py-3 rounded-[5px] font-inter-tight font-bold text-sm transition-colors ${
                isMarketActive
                  ? "bg-[#cb3131] hover:bg-[#b82a2a] text-white"
                  : "bg-[#18181D] border border-[#1A1A1A] text-gray-600 cursor-not-allowed"
              }`}
            >
              No {(buyNo * 100).toFixed(1)}¢
            </button>
          </div>
        </div>
      </div>

      {/* Section 2: Market Overview */}
      {sortedMarkets.length > 1 && (
        <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#1A1A1A]">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-gray-400" />
              <span className="text-white text-sm font-inter-tight font-bold">
                Market Overview
              </span>
            </div>
          </div>

          <div className="p-2 space-y-1">
            {sortedMarkets.map(({ market, originalIdx }, rank) => {
              const [mYes] = getRealtimePrice(market);
              const change = formatPriceChange(market.oneDayPriceChange);
              const isSelected = originalIdx === selectedIdx;

              return (
                <button
                  key={market.id}
                  onClick={() => setSelectedIdx(originalIdx)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-[5px] transition-colors text-left ${
                    isSelected
                      ? "bg-white/5 border border-gray-500"
                      : "bg-transparent hover:bg-white/5 border border-transparent"
                  }`}
                >
                  <span className="text-gray-500 text-xs font-inter-tight w-5 flex-shrink-0">
                    #{rank + 1}
                  </span>
                  <span className="text-white text-sm font-satoshi truncate flex-1 min-w-0">
                    {getMarketLabel(market)}
                  </span>
                  <span className="text-white font-inter-tight font-bold text-sm flex-shrink-0">
                    {Math.round(mYes * 100)}%
                  </span>
                  <span
                    className={`${change.color} text-xs font-inter-tight flex-shrink-0 flex items-center gap-0.5`}
                  >
                    <change.Icon className="w-3 h-3" />
                    {change.text}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Section 3: Related Markets */}
      {relatedLoading ? (
        <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] p-4">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-gray-500" />
            <span className="text-gray-400 text-sm font-inter-tight font-bold">
              Related Markets
            </span>
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-14 bg-[#0f0f12] rounded-[5px]" />
              </div>
            ))}
          </div>
        </div>
      ) : relatedEvents.length > 0 ? (
        <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#1A1A1A]">
            <div className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-gray-400" />
              <span className="text-white text-sm font-inter-tight font-bold">
                Related Markets
              </span>
            </div>
          </div>

          <div className="p-2 space-y-1">
            {relatedEvents.map((event) => {
              const topPrice =
                event.markets?.[0]?.outcomePrices?.[0];

              return (
                <Link
                  key={event.slug}
                  href={`/event/${event.slug}`}
                  onClick={() => { trackRecommendation(event.slug, 'view_detail', 'feed'); setNavigationSource('feed'); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-[5px] hover:bg-white/5 transition-colors"
                >
                  {(event.image || event.icon) && (
                    <div className="w-8 h-8 rounded-[5px] overflow-hidden flex-shrink-0 bg-[#0f0f12]">
                      <CdnImage
                        src={event.image || event.icon || ""}
                        alt={event.title}
                        width={32}
                        height={32}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-satoshi truncate">
                      {event.title}
                    </p>
                    {topPrice != null && (
                      <p className="text-gray-400 text-xs font-inter-tight">
                        Yes {Math.round(Number(topPrice) * 100)}%
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
